import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaGift, FaCheckCircle, FaCopy, FaWhatsapp, FaTwitter } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import LoadingPage from "../components/Loader";
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../components/NotificationManager';

const Referrals = () => {
  const [referralData, setReferralData] = useState(null);
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const fetchReferralCode = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Please log in to view referrals.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "https://mongobyte-hc73.onrender.com/api/v1/users/referral/generate",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferralData(response.data.data);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch referral code.");
      showErrorNotification(error.response?.data?.message || "Failed to fetch referral code.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReferralCode();
  }, []);

  const handleUseReferralCode = async (e) => {
    e.preventDefault();
    if (!referralCodeInput.trim()) {
      showErrorNotification("Please enter a referral code.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      showErrorNotification("Please log in to use a referral code.");
      return;
    }

    setUpdateLoading(true);
    try {
      const response = await axios.post(
        "https://mongobyte-hc73.onrender.com/api/v1/users/referral/use",
        { referralCode: referralCodeInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccessNotification(
        `${response.data.message}`
      );
      setReferralCodeInput("");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      showErrorNotification(error.response?.data?.message || "Failed to apply referral code.");
    }
    setUpdateLoading(false);
  };

  const copyToClipboard = () => {
    if (referralData?.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      showSuccessNotification("Referral code copied to clipboard!");
    }
  };

  const shareOnTwitter = () => {
    if (referralData?.referralCode) {
    //   const shareUrl = `https://yumbyte.ng/signup`;
      window.open(
        `https://x.com/intent/tweet?text=Join%20Byte%20at%20yumbyte.ng%20and%20get%20${referralData.bonusAmount}%20bytes%20with%20my%20code:%20${referralData.referralCode}!%20🍕 \n https://yumbyte.ng/`,
        "_blank"
      );
    }
  };
  const shareOnWhatsApp = () => {
    if (referralData?.referralCode) {
    //   const shareUrl = "https://yumbyte.ng/signup";
      const message = `Join Byte at https://yumbyte.ng/ and get ${referralData.bonusAmount} bytes to order food with my referral code: ${referralData.referralCode}! 🍕`;
      window.open(
        `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`,
        "_blank"
      );
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md"
        >
          <div className="text-pepperoni text-6xl mb-4">⚠️</div>
          <p className="text-pepperoni text-lg font-semibold mb-4">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchReferralCode}
            className="bg-pepperoni text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-6 px-4 pt-20 md:pt-24 pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -100 }}
              className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2"
            >
              <FaCheckCircle />
              <span>Referral code applied successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-crust rounded-3xl overflow-hidden shadow-2xl mb-8"
        >
          <div
            className="absolute inset-0 opacity-10 bg-repeat z-0"
            style={{ backgroundImage: "url('/Images/Band Pattern.jpg')", backgroundSize: "200px" }}
          />
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-pepperoni opacity-20 rounded-full blur-xl"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-cheese opacity-20 rounded-full blur-xl"></div>
          <div className="relative z-10 p-8 lg:p-12">
            <div className="text-center">
              <div className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-300 px-4 py-1 rounded-full mb-4">
                <h2 className="text-crust text-xl font-secondary tracking-wider font-bold">Refer a Friend</h2>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-secondary text-cheese drop-shadow-md">
                Invite Friends, Earn Bytes!
              </h1>
              <p className="text-white/90 font-sans text-lg max-w-xl mx-auto">
                Share your referral code with friends. When they complete their first order, you both earn {referralData?.rewardAmount || 300} bytes!
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 mb-8 shadow-xl"
        >
          <h3 className="text-2xl font-bold text-crust mb-6 text-center flex items-center justify-center gap-3">
            <FaGift className="text-pepperoni" />
            Your Referral Code
          </h3>
          {referralData && (
            <div className="space-y-6">
              <div className="bg-orange-50 rounded-2xl p-6 shadow-md border-l-4 border-cheese">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-mono text-crust">{referralData.referralCode}</span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={copyToClipboard}
                    className="bg-pepperoni text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-red-700 transition-colors"
                  >
                    <FaCopy />
                  </motion.button>
                </div>
                <div className="text-sm text-gray-600 space-y-2">
                  <p>
                    <strong>Reward:</strong> {referralData.rewardAmount} bytes per successful referral
                  </p>
                  <p>
                    <strong>Friend's Bonus:</strong> {referralData.bonusAmount} bytes
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <span
                      className={`capitalize ${
                        referralData.status === "pending"
                          ? "text-yellow-600"
                          : referralData.status === "completed"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {referralData.status}
                    </span>
                  </p>
                  <p>
                    <strong>Expires:</strong> {new Date(referralData.expiresAt).toLocaleDateString()}
                  </p>
                  {referralData.completedOrders !== undefined && (
                    <p>
                      <strong>Progress:</strong> {referralData.completedOrders}/{referralData.requiredOrders || 1} orders completed
                    </p>
                  )}
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={shareOnWhatsApp}
                className="w-full bg-cheese text-crust font-semibold py-4 px-6 rounded-xl shadow-lg flex items-center justify-center gap-3 hover:bg-yellow-400 transition-all"
              >
                <FaWhatsapp />
                Share on WhatsApp
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={shareOnTwitter}
                className="w-full bg-cheese text-crust font-semibold py-4 px-6 rounded-xl shadow-lg flex items-center justify-center gap-3 hover:bg-yellow-400 transition-all"
              >
                <FaTwitter />
                Share on X
              </motion.button>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 mb-8 shadow-xl"
        >
          <h3 className="text-2xl font-bold text-crust mb-6 text-center flex items-center justify-center gap-3">
            <FaGift className="text-pepperoni" />
            Use a Referral Code
          </h3>
          <form onSubmit={handleUseReferralCode} className="space-y-4">
            <div>
              <input
                type="text"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value)}
                placeholder="Enter referral code..."
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent hover:border-cheese transition-colors"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={updateLoading}
              className="w-full bg-pepperoni text-white font-semibold py-4 px-6 rounded-xl shadow-lg flex items-center justify-center gap-3 hover:bg-red-700 transition-all disabled:bg-gray-400"
            >
              <FaCheckCircle />
              {updateLoading ? "Applying..." : "Apply Referral Code"}
            </motion.button>
            <p className="text-sm text-gray-600 text-center">
              Enter a friend's code to earn {referralData?.bonusAmount || 300} bytes after your first order!
            </p>
          </form>
        </motion.div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Referrals;